import React, { FC } from 'react';
import { connect } from 'react-redux';

import { CurrencyCode } from 'app/api/services/site-configs/types';
import { ReduxState } from 'app/redux/types';

import { DictionaryData, useTranslation } from 'app/lang';
import { StakeType } from 'app/lib/staking/constants';
import selectCurrency from 'app/redux/selectors/сurrency';

import { AmountValidationResult, SubmissionState } from '../../../../constants';
import { RecordOfEntryErrorType } from '../../../../records';
import { selectIsConflictedEntry } from '../../../../selectors/conflict';
import {
  selectLockedBetErrorMessage,
  selectRequestErrorShowing,
  selectTotalAmountActive,
} from '../../../../selectors/entries';
import { selectCurrentStakeType } from '../../../../selectors/stakeTypes';
import {
  selectBetInfoSubmissionByBetId,
  selectEntryAmountValidation,
  selectSingleBetsInfo,
  selectSingleEntryIdsWithTheLargestMinLimit,
} from '../../../../selectors/stakeTypes/single';
import Error from '../../../EntryComponents/Error';
import TextWithAmountBar from '../../../TextWithAmountBar';

import dictionary from './Error.dictionary.json';

// ---

type StoreProps = {
  disabled: boolean;
  isConflictedEntry: boolean;
  isLockedMessage: DictionaryData | null;
  error?: RecordOfEntryErrorType | null;
  showRequestError: boolean;
  showFooter: boolean;
  currency: CurrencyCode;
  showMinLimitError: boolean;
  minValue: number;
};

type EntryErrorContentProps = {
  betId: number;
};

const EntryErrorContent: FC<EntryErrorContentProps & StoreProps> = ({
  disabled,
  isConflictedEntry,
  isLockedMessage,
  error,
  showRequestError,
  showFooter,
  currency,
  showMinLimitError,
  minValue,
}) => {
  const { t } = useTranslation();
  if (disabled) {
    return null;
  }

  if (isConflictedEntry) {
    return (
      <Error message={t(dictionary.errorMessage)}
             data-test="entry-error" />
    );
  }

  if (isLockedMessage) {
    return (
      <Error message={isLockedMessage}
             data-test="entry-error" />
    );
  }

  if (showRequestError) {
    return (
      <Error error={error}
             currency={currency}
             data-test="entry-error" />
    );
  }

  if (!showFooter && showMinLimitError) {
    return (
      <TextWithAmountBar hasError
                         data-test="entry-error"
                         value={minValue}
                         label={dictionary.label} />
    );
  }

  return null;
};

const mapStateToProps = (state: ReduxState, { betId }: EntryErrorContentProps): StoreProps => {
  const betInfo = selectSingleBetsInfo(state).get(betId);
  const totalAmountActive = selectTotalAmountActive(state);

  const minValue = betInfo?.limits.min || 0;
  const isSingle = selectCurrentStakeType(state).has(StakeType.SINGLE);

  const validation = selectEntryAmountValidation(state, betId);
  let showMinLimitError = false;

  if (totalAmountActive) {
    const { entryIds } = selectSingleEntryIdsWithTheLargestMinLimit(state);

    if (entryIds.has(betId)) {
      showMinLimitError = true;
    }
  }
  else {
    showMinLimitError = isSingle && validation === AmountValidationResult.MIN_NOT_REACHED;
  }

  return {
    disabled: selectBetInfoSubmissionByBetId(state, betId) !== SubmissionState.AWAITING,
    isConflictedEntry: selectIsConflictedEntry(state, betId),
    isLockedMessage: selectLockedBetErrorMessage(state, betId),
    error: betInfo?.get('error'),
    showRequestError: selectRequestErrorShowing(state, betId),
    showFooter: !selectTotalAmountActive(state) && isSingle,
    currency: selectCurrency(state),
    minValue,
    showMinLimitError,
  };
};

export default connect(mapStateToProps)(EntryErrorContent);
